import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Clear, Light } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  OutlinedInput,
  InputAdornment,
  TablePagination,
  Theme,
  Typography,
  useTheme,
  Select,
  SelectChangeEvent,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { IconButton } from '@mui/material';
import { GridColDef, DataGridPro, GridRowParams } from '@mui/x-data-grid-pro';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import * as clientSlice from '../../../data/client.slice';
import * as lookupSlice from '../../../data/lookup.slice';

import PageHeader from '../../controls/PageHeader';
import TransportationBenefitDialog from './transportation-benefit/TransportationBenefitDialog';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';

const statusColors = (theme: Theme) => ({
  active: {
    backgroundColor: theme.palette.statusBackground.main,
    color: theme.palette.statusActive.main,
  },
  // TODO: change colors for inactive and draft
  inactive: {
    backgroundColor: theme.palette.statusBackground.main,
    color: theme.palette.statusActive.main,
  },
  draft: {
    backgroundColor: theme.palette.statusBackground.main,
    color: theme.palette.statusActive.main,
  },
});

const PREFIX = 'ClientsTransportationBenefitsList';

const classes = {
  container: `${PREFIX}-container`,
  cardMain: `${PREFIX}-cardMain`,
  cardHeader: `${PREFIX}-cardHeader`,
  toolbar: `${PREFIX}-toolbar`,
  toolContainer: `${PREFIX}-toolContainer`,
  toolInput: `${PREFIX}-toolInput`,
  grid: `${PREFIX}-grid`,
  statusCellContainer: `${PREFIX}-statusCellContainer`,
  statusCell: `${PREFIX}-statusCell`,
  statusText: `${PREFIX}-statusText`,
  statusDate: `${PREFIX}-statusDate`,
  toolSelect: `${PREFIX}-toolSelect`,
  toolSelectLabel: `${PREFIX}-toolSelectLabel`,
  transportationBenefitName: `${PREFIX}-transportationBenefitName`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [`& .${classes.cardMain}`]: {
    width: '100%',
    padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 13,
  },
  [`& .${classes.cardHeader}`]: {
    padding: `${theme.spacing(1)} 0`,
    '& .MuiCardHeader-title': {
      fontSize: 30,
      fontWeight: 700,
      fontFamily: 'Bitter',
      lineHeight: '40px',
      letterSpacing: '0.25px',
      color: theme.palette.lightTextPrimary.main,
    },
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
  },
  [`& .${classes.toolContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.toolInput}`]: {
    width: 327,
    maxWidth: 327,
    borderColor: 'transparent !important',
    borderRadius: '3px',
    background: '#FFF',
    boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolInput} fieldset`]: {
    display: 'none',
  },
  [`& .${classes.toolInput} input`]: {
    color: theme.palette.lightActionDisabled60p.main,
    padding: `${theme.spacing(1.5)} 0`,

    '&::placeholder': {
      color: theme.palette.lightActionDisabled60p.main,
    },
  },
  [`& .${classes.toolInput} .MuiSelect-select`]: {
    padding: theme.spacing(1.5),
    color: '#000',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    flexGrow: 'unset',
    height: 'fit-content',
    '&.empty': {
      height: 300,
    },
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root':
      {
        width: '100%',
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 24,
        letterSpacing: 0.17,
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    [`& .MuiDataGrid-row`]: {
      letterSpacing: '0.17px',
      cursor: 'pointer',
      '& .MuiDataGrid-cell:last-child': {
        flex: 1,
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
  [`& .${classes.statusCellContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  [`& .${classes.statusCell}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 64,
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    width: 'fit-content',

    [`& .${classes.statusText}, & .${classes.statusDate}`]: {
      fontFamily: 'Source Sans Pro',
      fontSize: 12,
      fontStyle: 'normal',
      lineHeight: '100%',
      letterSpacing: 0.17,
    },
    [`& .${classes.statusText}`]: {
      fontWeight: 600,
    },
    [`& .${classes.statusDate}`]: {
      fontWeight: 400,
    },
  },
  [`& .${classes.toolSelect}`]: {
    width: 155,
    color: theme.palette.lightTextPrimary.main,
    fontFamily: 'Source Sans Pro',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.toolSelectLabel}`]: {
    color: theme.palette.lightActionDisabled60p.main,
    fontFamily: 'Source Sans Pro',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '12px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.transportationBenefitName}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    fontSize: '14px',
    letterSpacing: '0.17px',
    lineHeight: '20px',
  },
}));

export default function ClientTransportationBenefitsList() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const transportationBenefitsList = useAppSelector(
    clientSlice.selectTransportationBenefits,
  );
  const loadingTransportationBenefitsList = useAppSelector(
    clientSlice.selectLoadingTransportationBenefits,
  );
  const benefitPurposes = useAppSelector(lookupSlice.selectBenefitPurposes);
  const linesOfBusiness = useAppSelector(lookupSlice.selectLinesOfBusiness);
  const client = useAppSelector(clientSlice.selectCurrentClient);
  const theme = useTheme();

  const isSuperReaderUser = useIsSuperReaderUser();

  const [searchQuery, setSearchQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [transportationBenefitDialogOpen, setTransportationBenefitDialogOpen] =
    React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [selectedTransportationBenefit, setSelectedTransportationBenefit] =
    React.useState<TransportationBenefit | undefined>(undefined);

  React.useEffect(() => {
    if (client?.clientBC) {
      dispatch(clientSlice.fetchTransportationBenefitsAsync(client.clientBC));
    }
  }, [client?.clientBC, dispatch]);

  const filteredTransportationBenefits = useMemo(() => {
    return transportationBenefitsList.filter((transportationBenefit) => {
      const matchesSearch = [
        transportationBenefit.name,
        transportationBenefit.description,
        benefitPurposes.find(
          (benefitPurpose) =>
            benefitPurpose.benefitPurposeBC ===
            transportationBenefit.benefitPurposeBC,
        )?.benefitPurposeName,
        linesOfBusiness.find(
          (lineOfBusiness) =>
            lineOfBusiness.lineOfBusinessBC ===
            transportationBenefit.lineOfBusinessBC,
        )?.lineOfBusinessName,
      ].some((field) =>
        (field || '').toLowerCase().includes(searchQuery.toLowerCase()),
      );

      const matchesStatus =
        statusFilter === 'all' || transportationBenefit.state === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [transportationBenefitsList, searchQuery, statusFilter]);

  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const paginatedTransportationBenefits = useMemo(() => {
    return filteredTransportationBenefits.slice(startIndex, endIndex);
  }, [filteredTransportationBenefits, startIndex, endIndex]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
      setPage(1);
    },
    [setSearchQuery],
  );

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };

  const handleCreateTransportationBenefit = () => {
    setSelectedTransportationBenefit(undefined);
    setTransportationBenefitDialogOpen(true);
  };

  const handleTransportationBenefitDialogClose = () => {
    setTransportationBenefitDialogOpen(false);
  };

  const handleClickRow = (params: GridRowParams<TransportationBenefit>) => {
    setSelectedTransportationBenefit(params.row);
    setTransportationBenefitDialogOpen(true);
  };

  const columns: GridColDef<TransportationBenefit>[] = [
    {
      field: 'name',
      headerName: t('client.transportationBenefitName'),
      sortable: false,
      width: 296,
      renderCell: (params) => (
        <Box className={classes.transportationBenefitName}>
          <span style={{ color: theme.palette.lightActionDisabled90p.main }}>
            {params.row.name}{' '}
          </span>
          <span style={{ color: theme.palette.lightActionDisabled60p.main }}>
            {params.row.description}
          </span>
        </Box>
      ),
    },
    {
      field: 'benefitPurpose',
      headerName: t('client.benefitPurpose'),
      sortable: false,
      width: 147,
      renderCell: (params) => (
        <span style={{ color: theme.palette.lightActionDisabled90p.main }}>
          {
            benefitPurposes.find(
              (benefitPurpose) =>
                benefitPurpose.benefitPurposeBC === params.row.benefitPurposeBC,
            )?.benefitPurposeName
          }{' '}
        </span>
      ),
    },
    {
      field: 'lineOfBusiness',
      headerName: t('client.lineOfBusiness'),
      sortable: false,
      width: 139,
      renderCell: (params) => (
        <span style={{ color: theme.palette.lightActionDisabled90p.main }}>
          {
            linesOfBusiness.find(
              (lineOfBusiness) =>
                lineOfBusiness.lineOfBusinessBC === params.row.lineOfBusinessBC,
            )?.lineOfBusinessName
          }{' '}
        </span>
      ),
    },
    {
      field: 'priority',
      headerName: t('client.priority'),
      sortable: false,
      align: 'right',
      width: 120,
      renderHeader: () => (
        <Typography align="right">{t('client.priority')}</Typography>
      ),
      renderCell: (params) => {
        return <>{params.row.priority}</>;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 251,
      align: 'center',
      sortable: false,
      renderHeader: () => (
        <Typography align="center">{t('client.status')}</Typography>
      ),
      renderCell: (params) => (
        <Box className={classes.statusCellContainer}>
          <Box
            className={classes.statusCell}
            sx={
              statusColors(theme)[
                (params.row.state || 'draft') as keyof typeof statusColors
              ]
            }>
            <Typography className={classes.statusText}>
              {(params.row.state || 'draft').charAt(0).toUpperCase() +
                (params.row.state || 'draft').slice(1)}
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <StyledBox className={classes.container} maxWidth="xl">
      <Card className={classes.cardMain}>
        <PageHeader
          title={`${t('client.transportationBenefits')}`}
          returnLink={`/clients/${client?.clientBC}`}
          returnLinkText={`${client?.legalName} ${t('client.client')}`}
        />
        <CardContent>
          <Box className={classes.toolbar}>
            <Box className={classes.toolContainer}>
              <OutlinedInput
                size="small"
                className={classes.toolInput}
                placeholder={t('client.search')}
                value={searchQuery}
                onChange={handleSearchChange}
                startAdornment={
                  <InputAdornment position="start" sx={{ marginLeft: '20px' }}>
                    {searchQuery ? (
                      <IconButton
                        edge="start"
                        onClick={() => {
                          setSearchQuery('');
                          setPage(1);
                        }}>
                        <Clear />
                      </IconButton>
                    ) : (
                      <IconButton edge="start">
                        <Search />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />
              <FormControl sx={{ width: 'fit-content' }}>
                <InputLabel
                  variant="standard"
                  htmlFor="status-select"
                  className={classes.toolSelectLabel}>
                  {t('client.status')}
                </InputLabel>
                <Select
                  value={statusFilter}
                  className={classes.toolSelect}
                  onChange={handleStatusFilterChange}>
                  <MenuItem value="all">{t('client.all')}</MenuItem>
                  <MenuItem value="active">{t('client.active')}</MenuItem>
                  <MenuItem value="inactive">{t('client.inactive')}</MenuItem>
                  <MenuItem value="draft">{t('client.draft')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {!isSuperReaderUser && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateTransportationBenefit}>
                {t('client.add')}
              </Button>
            )}
          </Box>
          <DataGridPro
            loading={loadingTransportationBenefitsList}
            rows={paginatedTransportationBenefits}
            getRowId={(row) => row.transportationBenefitUUID || ''}
            columns={columns}
            sortingMode="server"
            className={`${classes.grid} ${filteredTransportationBenefits.length === 0 ? 'empty' : ''}`}
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            hideFooterRowCount
            hideFooter
            rowHeight={56}
            disableColumnMenu={true}
            disableColumnResize={true}
            onRowClick={handleClickRow}
          />
          <TablePagination
            component="div"
            count={filteredTransportationBenefits.length}
            page={page - 1}
            onPageChange={(event, page) => setPage(page + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(Number.parseInt(event.target.value, 10));
              setPage(1);
            }}
          />
          {!!client && (
            <TransportationBenefitDialog
              title={
                !!selectedTransportationBenefit
                  ? t('client.editTransportationBenefit')
                  : t('client.addTransportationBenefit')
              }
              clientBC={client?.clientBC}
              transportationBenefit={selectedTransportationBenefit}
              open={transportationBenefitDialogOpen}
              close={handleTransportationBenefitDialogClose}
            />
          )}
        </CardContent>
      </Card>
    </StyledBox>
  );
}
